import { PatchRequest } from 'app/shared/patch-request.model';
import { roleOf, SiteMembershipRole, SiteRole, siteRolesOf } from 'app/core/identity/site-role';

/**
 * The user fields the gateway's user PATCH routes accept. Sites travel as one add or remove per site id. Superadmin is a
 * global role, given with `setGlobalRole`.
 */
export interface UserPatchFields {
  fullName?: string;
  language?: string;
  timeZoneId?: string;
  isEnabled?: boolean;
  isSiteAdmin?: boolean;
  darkMode?: string;
  hasEarlyAccess?: boolean;
  earlyAccessFeatures?: string[];
  siteIds?: number[];
}

type ReplaceableField = Exclude<keyof UserPatchFields, 'siteIds'>;

const REPLACEABLE_FIELDS: ReplaceableField[] = [
  'fullName',
  'language',
  'timeZoneId',
  'isEnabled',
  'isSiteAdmin',
  'darkMode',
  'hasEarlyAccess',
  'earlyAccessFeatures',
];

/**
 * JSON Patch operations for the fields `after` sets that differ from `before`, so a save never writes back a field
 * someone else may have changed meanwhile. An empty string is never sent, because the server cannot clear a field.
 */
export function userPatch(before: UserPatchFields, after: UserPatchFields): PatchRequest[] {
  const operations: PatchRequest[] = [];
  for (const field of REPLACEABLE_FIELDS) {
    const value = after[field];
    if (value === undefined || value === null || value === '' || isSame(before?.[field], value)) {
      continue;
    }
    operations.push(replaceUserField(field, value));
  }

  if (after.siteIds) {
    const { added, removed } = siteChanges(before?.siteIds, after.siteIds);
    operations.push(...addSites(added), ...removeSites(removed));
  }
  return operations;
}

/**
 * The edit dialog's save. When it returns `sites`, the role on each site is compared instead of the flag, which would set
 * every site.
 */
export function editedUserPatch(
  before: UserPatchFields & { isSiteAdmin: boolean; sites?: SiteRole[] },
  after: UserPatchFields & { sites?: SiteRole[] },
): PatchRequest[] {
  if (!after.sites) {
    return userPatch(before, after);
  }
  return [
    ...userPatch(before, { ...after, isSiteAdmin: undefined, siteIds: undefined }),
    ...siteRolesPatch(siteRolesOf(before), after.sites),
  ];
}

/**
 * The account admin change once roles may differ between sites: the role is set on the open site only, and each added
 * site gets the chosen role.
 */
export function siteRolePatch(
  openSiteId: number,
  before: { isSiteAdmin: boolean; siteIds: number[] },
  after: { isSiteAdmin: boolean; siteIds: number[] },
): PatchRequest[] {
  const operations: PatchRequest[] = [];
  if (before.isSiteAdmin !== after.isSiteAdmin && after.siteIds.some((siteId) => +siteId === +openSiteId)) {
    operations.push(setSiteRole(openSiteId, after.isSiteAdmin));
  }
  const { added, removed } = siteChanges(before.siteIds, after.siteIds);
  operations.push(...addSites(added, after.isSiteAdmin), ...removeSites(removed));
  return operations;
}

export function siteRolesPatch(before: SiteRole[], after: SiteRole[]): PatchRequest[] {
  const beforeRoles = new Map(before.map((site) => [+site.siteId, site.role]));
  const afterRoles = new Map(after.map((site) => [+site.siteId, site.role]));
  const added = [...afterRoles].filter(([siteId]) => !beforeRoles.has(siteId) && siteId > 0);
  const removed = [...beforeRoles.keys()].filter((siteId) => !afterRoles.has(siteId));
  const changed = [...afterRoles].filter(
    ([siteId, role]) => beforeRoles.has(siteId) && beforeRoles.get(siteId) !== role,
  );
  return [
    ...added.map(([siteId, role]) => addSite(siteId, role === SiteMembershipRole.AccountAdmin)),
    ...removeSites(removed),
    ...changed.map(([siteId, role]) => setSiteRole(siteId, role === SiteMembershipRole.AccountAdmin)),
  ];
}

export function replaceUserField<T extends ReplaceableField>(field: T, value: UserPatchFields[T]): PatchRequest {
  return { op: 'replace', path: `/${field}`, value };
}

export function setGlobalRole(role: string, has: boolean): PatchRequest {
  return { op: has ? 'add' : 'remove', path: `/roles/${role}` };
}

export function setSiteRole(siteId: number, isAccountAdmin: boolean): PatchRequest {
  return { op: 'replace', path: `/sites/${siteId}/role`, value: roleOf(isAccountAdmin) };
}

/** Without a role, Identity gives each site the role the user has on all their sites. */
export function addSites(siteIds: number[], isAccountAdmin?: boolean): PatchRequest[] {
  return realSiteIds(siteIds).map((siteId) => addSite(siteId, isAccountAdmin));
}

function addSite(siteId: number, isAccountAdmin?: boolean): PatchRequest {
  const value = isAccountAdmin === undefined ? {} : { role: roleOf(isAccountAdmin) };
  return { op: 'add', path: `/sites/${siteId}`, value };
}

export function removeSites(siteIds: number[]): PatchRequest[] {
  return realSiteIds(siteIds).map((siteId) => ({ op: 'remove', path: `/sites/${siteId}` }));
}

function siteChanges(before: number[] | undefined, after: number[]): { added: number[]; removed: number[] } {
  const beforeSiteIds = new Set((before ?? []).map(Number));
  const afterSiteIds = new Set(after.map(Number));
  return {
    added: [...afterSiteIds].filter((siteId) => !beforeSiteIds.has(siteId)),
    removed: [...beforeSiteIds].filter((siteId) => !afterSiteIds.has(siteId)),
  };
}

// The -1 placeholder is Identity's to manage, so it is never added or removed by id.
function realSiteIds(siteIds: number[]): number[] {
  return [...new Set((siteIds ?? []).map(Number))].filter((siteId) => siteId > 0);
}

function isSame(before: unknown, after: unknown): boolean {
  if (Array.isArray(after)) {
    const beforeItems: unknown[] = Array.isArray(before) ? before : [];
    return beforeItems.length === after.length && beforeItems.every((item) => after.includes(item));
  }
  return before === after;
}
